import { Logger } from "@/lib/logger/Logger";
import { supabase } from "@/lib/supabase";
import {
	FileWithShare,
	JobDocumentEntityType,
	JobItemEntityType,
	JobItemEntityTypeInsert,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { StateCreator } from "zustand";
import { useCentralStore } from "../Central";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import {
	GsStatusEnum,
	KvStatusEnum,
	LsStatusEnum,
	PermissionStatus,
} from "../../hooks/actions/actions-hooks-types";
import { JobStatusEnum } from "@/lib/types/job";
import { JobStoreUnion } from ".";
import { v4 as uuidv4 } from "uuid";
import { showNotification } from "../Central/selectors";
import { produce } from "immer";
import {
	formatArticle,
	tariffToJobItem,
	jobDocumentTypeNamePresets,
	adjustJobStage,
	documentDrivesJobStatus,
} from "./utils";
import { useTemplatesStore } from "../Templates/templates.store";
import { useCatalogsStore, TariffWithPricesViewType } from "../Catalogs";
import { resolveRegionKind } from "../../lib/utils/region-utils";

export type JobDocumentWithFiles = JobDocumentEntityType & {
	files_with_shares: FileWithShare[];
};

type OpenedSidebarType = "tariff" | "article" | "template" | "catalog";

interface JobDocumentsState {
	// TODO(?): Could force this to be a string by just treaing it this way everywhere with .toString()
	jobDocuments: Record<number, JobDocumentWithFiles>;
	selectedDocumentId: number | null;
	openedSidebar: null | {
		documentId: number;
		type: OpenedSidebarType;
	};
}

export const initialJobDocumentsState: JobDocumentsState = {
	jobDocuments: {},
	selectedDocumentId: null,
	openedSidebar: null,
};

const getJobClientWithRegion = (clientId: string | null | undefined) => {
	const { clientsLookup, organization, activatedRegions } =
		useCentralStore.getState();
	const client = clientId ? clientsLookup[clientId] : undefined;
	return {
		client,
		region: resolveRegionKind(client, organization, activatedRegions),
	};
};

export interface JobDocumentSlice extends JobDocumentsState {
	createJobDocument: (
		type: JobDocumentTypeEnum
	) => Promise<number | undefined>;
	createJobDocumentFromTemplate: (
		jobDocumentTemplateId: number,
		templateType: JobDocumentTypeEnum
	) => Promise<number | undefined>;
	isCreateJobDocumentAllowed: (
		jobDocumentType: JobDocumentTypeEnum
	) => PermissionStatus;
	deleteJobDocumentLocally: (id: number) => void;
	isDeleteJobDocumentAllowed: (id: number) => PermissionStatus;
	fetchJobDocument: (id: number) => Promise<JobDocumentWithFiles | null>;
	updateJobDocumentDate: (
		documentId: number,
		status: JobStatusEnum,
		date: string
	) => Promise<void>;
	duplicateJobDocument: (
		jobDocument: JobDocumentEntityType,
		existingJobItems: JobItemEntityType[],
		newDocumentType?: JobDocumentTypeEnum
	) => Promise<number | undefined>;
	updateJobDocumentTitle: (
		id: number,
		title: string,
		status: JobStatusEnum
	) => Promise<void>;
	updateJobDocumentNote: (
		id: number,
		note: string | null,
		status: JobStatusEnum
	) => Promise<void>;
	updateJobDocumentStatus: (
		documentId: number,
		status: JobStatusEnum | KvStatusEnum | LsStatusEnum | GsStatusEnum,
		amount?: number | null,
		accountingDocumentId?: number | null
	) => Promise<boolean>;
	openSideBar: (documentId: number, type: OpenedSidebarType) => void;
	closeSidebar: () => void;
	setSelectedDocumentId: (id: number | null) => void;
	updateDocumentDiscount: (
		documentId: number,
		field: "discount_work" | "discount_material",
		value: number
	) => Promise<void>;
}

export const createJobDocumentStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	JobDocumentSlice
> = (set, get) => ({
	...initialJobDocumentsState,
	openedSidebar: null,
	createJobDocument: async (type: JobDocumentTypeEnum) => {
		const permission = get().isCreateJobDocumentAllowed(type);

		if (!permission.isAllowed) {
			showNotification({
				message: permission.explanation,
				type: "error",
			});

			Logger.error(permission.explanation);
			return;
		}

		const { client, region } = getJobClientWithRegion(get().job?.client_id);

		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.insert({
				title: jobDocumentTypeNamePresets(type, region),
				// These variables are only being used here, no need to assign
				// explicit names
				job_id: get().job?.id,
				type,
				discount_material: client?.discount_material ?? 0,
				discount_work: client?.discount_work ?? 0,
			} as JobDocumentEntityType)
			.select()
			.single();

		if (error) {
			Logger.error(error);
			return;
		}

		if (data) {
			// const currentJob = get().job;
			// const wasPreRegistered =
			// 	currentJob?.status === JobStatusEnum.PRE_REGISTERED;

			set(
				produce((state) => {
					state.jobDocuments[data.id] = {
						...data,
						files_with_shares: [],
					};
					const newTypes = [
						...new Set([
							...(state.job.job_document_types || []),
							type,
						]),
					];
					state.job.job_document_types = newTypes;
					// if (wasPreRegistered) {
					// 	adjustJobStatus(
					// 		state,
					// 		state.job.id,
					// 		JobStatusEnum.IN_PROGRESS,
					// 		JobStatusEnum.PRE_REGISTERED
					// 	);
					// }
					adjustJobStage(state, state.job.id, newTypes);
				})
			);

			// if (wasPreRegistered && currentJob?.id) {
			// 	await supabase
			// 		.from(SupabaseTableEnum.JOBS)
			// 		.update({ status: JobStatusEnum.IN_PROGRESS })
			// 		.eq("id", currentJob.id);
			// }

			if (documentDrivesJobStatus(type)) {
				await get().syncJobStatus(data.job_id);
			}

			return data.id;
		}
	},
	createJobDocumentFromTemplate: async (
		templateId: number,
		templateType: JobDocumentTypeEnum
	) => {
		const job = get().job;
		if (!job) {
			Logger.error("createJobDocumentFromTemplate - Job id is not set");
			showNotification({
				message:
					"Fehler beim Erstellen des Dokuments aus Vorlage (Auftrag nicht gefunden)",
				type: "error",
			});
			return undefined;
		}

		const pricing = get().requireJobPricing("template-document");
		if (!pricing) return undefined;

		const { name: templateName, template_items: templateItems } =
			useTemplatesStore.getState().templatesLookup[templateId];
		const newJobDocumentId = await get().createJobDocument(templateType);

		if (!newJobDocumentId) {
			showNotification({
				message:
					"Fehler beim Erstellen des Dokuments aus Vorlage (Dokument nicht erstellt)",
				type: "error",
			});
			Logger.error(
				"createJobDocumentFromTemplate - Fehler beim Erstellen des Dokuments"
			);
			return undefined;
		}

		// shitty because it doesn't really check whether it's an article or a tariff
		const aShitWayToGetTheFormattedItem = (code_e: string) => {
			// Check if code_e is a tariff or article
			const tariffsForCode =
				useCatalogsStore.getState().tariffsLookup[code_e];
			const articlesForCode = useCentralStore.getState().articles[code_e];

			// Get the first tariff or article for this code_e
			let tariff: TariffWithPricesViewType | undefined = undefined;
			if (tariffsForCode && Object.keys(tariffsForCode).length > 0) {
				let itemsToConsider = Object.values(tariffsForCode);
				// If there are exactly 2 tariffs and one is custom and one is default, prefer the custom one
				if (
					itemsToConsider.length === 2 &&
					itemsToConsider.some((item) => item.is_custom) &&
					itemsToConsider.some((item) => !item.is_custom)
				) {
					itemsToConsider = itemsToConsider.filter(
						(item) => item.is_custom
					);
				}
				tariff = itemsToConsider[0];
			}
			const article =
				articlesForCode && Object.keys(articlesForCode).length > 0
					? Object.values(articlesForCode)[0]
					: undefined;

			if (tariff) {
				return tariffToJobItem(
					tariff,
					newJobDocumentId,
					pricing.priceListId,
					pricing.tpValue,
					job.tp_variation ?? 0
				);
			} else if (article) {
				return formatArticle(article, newJobDocumentId);
			} else {
				return null;
			}
		};

		const itemsToInsert = templateItems.map((item) => {
			const formattedItem = aShitWayToGetTheFormattedItem(item.code_e);

			if (!formattedItem?.data || formattedItem.error) {
				showNotification({
					message: `Position ${item.code_e} aus Jumbo existiert nicht und wurde übersprungen.`,
					type: "warning",
				});
				return null;
			}

			return {
				...formattedItem.data,
				quantity: item.quantity ?? 1,
			};
		});

		const filteredItemsToInsert = itemsToInsert.filter(
			(item): item is NonNullable<typeof item> => item !== null
		);

		const defaultTechnicianId = get().defaultTechnician;
		const technicianAppliesToArticles =
			(
				useCentralStore.getState().organization?.config as
					| Record<string, unknown>
					| null
					| undefined
			)?.technician_applies_to_articles === true;

		if (defaultTechnicianId) {
			for (const item of filteredItemsToInsert) {
				if (
					!item.profile_id &&
					(technicianAppliesToArticles ||
						item.type === JobItemTypeEnum.TARIFF)
				) {
					item.profile_id = defaultTechnicianId;
				}
			}
		}

		if (filteredItemsToInsert.length === 0) {
			showNotification({
				message:
					"Keine Positionen aus Vorlage konnten erstellt werden.",
				type: "error",
			});
			return undefined;
		}

		const { error: jobItemsError } = await supabase
			.from(SupabaseTableEnum.JOB_ITEMS)
			.insert(filteredItemsToInsert);

		if (jobItemsError) {
			Logger.error(
				"createJobDocumentFromTemplate - Fehler beim Erstellen der Positionen aus Vorlage",
				{},
				jobItemsError
			);
			showNotification({
				message: "Fehler beim Erstellen der Positionen aus Vorlage",
				type: "error",
			});
			return undefined;
		}

		Logger.log(
			"createJobDocumentFromTemplate - [createJobDocumentFromTemplate 2]",
			newJobDocumentId
		);

		if (newJobDocumentId) {
			get().fetchJobDocument(newJobDocumentId);
		}

		get().pendingChangesNotification();

		// update job title if the template has a name
		if (templateName) {
			get().updateJob("title", templateName);
		}
		return newJobDocumentId;
	},
	duplicateJobDocument: async (
		jobDocument: JobDocumentEntityType,
		existingJobItems: JobItemEntityType[],
		newDocumentType?: JobDocumentTypeEnum
	) => {
		if (!jobDocument || !jobDocument.type) {
			showNotification({
				message: "Dokument konnte nicht kopiert werden.",
				type: "error",
			});
			return;
		}

		const jobDocumentType =
			newDocumentType || (jobDocument.type as JobDocumentTypeEnum);

		const permission = get().isCreateJobDocumentAllowed(jobDocumentType);

		if (permission.isAllowed === false) {
			showNotification({
				message: permission.explanation,
				type: "error",
			});
			return;
		}

		const { error: jobDocumentError, data: jobDocumentData } =
			await supabase
				.from(SupabaseTableEnum.JOB_DOCUMENTS)
				.insert({
					title: jobDocumentTypeNamePresets(
						jobDocumentType,
						getJobClientWithRegion(get().job?.client_id).region
					),
					job_id: jobDocument.job_id,
					type: jobDocumentType,
					discount_material: jobDocument.discount_material,
					discount_work: jobDocument.discount_work,
					note: jobDocument.note,
				})
				.select()
				.single();

		if (jobDocumentError) {
			showNotification({
				message: "Datenbankfehler beim Duplizieren des Dokuments.",
				type: "error",
			});
			Logger.error(jobDocumentError);
			return;
		}

		if (jobDocumentData && existingJobItems.length > 0) {
			const { error: jobItemsError } = await supabase
				.from(SupabaseTableEnum.JOB_ITEMS)
				.insert(
					existingJobItems.map(
						(item): JobItemEntityTypeInsert => ({
							id: uuidv4(),
							job_document_id: jobDocumentData.id,
							code: item.code,
							code_e: item.code_e,
							type: item.type,
							quantity: item.quantity ?? 1,
							price: item.price ?? 0,
							tp_value: item.tp_value ?? 0,
							discount: item.discount ?? 0,
							description: item.description,
							note: item.note,
							additional_text: item.additional_text,
							is_print_additional_text:
								item.is_print_additional_text,
							is_warranty: item.is_warranty,
							is_req_dr_tax: item.is_req_dr_tax,
							legacy_tax_rate: item.legacy_tax_rate,
							lot: item.lot,
							cluster: item.cluster,
							manufacturer: item.manufacturer,
							composition: item.composition,
							ce_number: item.ce_number,
							profile_id: item.profile_id,
							profile2_id: item.profile2_id,
							profile3_id: item.profile3_id,
							techn_perc_2: item.techn_perc_2,
							techn_perc_3: item.techn_perc_3,
							fpo_id: item.fpo_id,
							legacy_article_id: item.legacy_article_id,
							legacy_tariff_id: item.legacy_tariff_id,
						})
					)
				);

			if (jobItemsError) {
				showNotification({
					message: "Fehler beim Kopieren der Positionen.",
					type: "error",
				});
				Logger.error(jobItemsError);
				return;
			}
		}

		const newJobDocument: JobDocumentWithFiles = {
			...jobDocumentData,
			files_with_shares: [],
		};

		// const currentJob = get().job;
		// const wasPreRegistered =
		// 	currentJob?.status === JobStatusEnum.PRE_REGISTERED;

		set(
			produce((state) => {
				state.jobDocuments[jobDocumentData.id] = newJobDocument;
				const newTypes = [
					...new Set([
						...(state.job.job_document_types || []),
						jobDocumentType,
					]),
				];
				state.job.job_document_types = newTypes;
				// if (wasPreRegistered) {
				// 	adjustJobStatus(
				// 		state,
				// 		state.job.id,
				// 		JobStatusEnum.IN_PROGRESS,
				// 		JobStatusEnum.PRE_REGISTERED
				// 	);
				// }
				adjustJobStage(state, state.job.id, newTypes);
			})
		);

		// if (wasPreRegistered && currentJob?.id) {
		// 	await supabase
		// 		.from(SupabaseTableEnum.JOBS)
		// 		.update({ status: JobStatusEnum.IN_PROGRESS })
		// 		.eq("id", currentJob.id);
		// }

		if (documentDrivesJobStatus(jobDocumentType)) {
			await get().syncJobStatus(jobDocumentData.job_id);
		}

		get().pendingChangesNotification();

		return jobDocumentData.id;
	},

	/**
	 * isCreateJobDocumentAllowed - Function to check if a job document can be created
	 * @param {JobDocumentTypeEnum} jobDocumentType
	 * @returns {PermissionStatus}
	 *
	 * ALLOWED:
	 * - Quotation: Always
	 * - DeliveryNote: If no delivery note (that's not archived) exists
	 * - CreditNote: Always
	 */
	isCreateJobDocumentAllowed: (
		jobDocumentType: JobDocumentTypeEnum
	): PermissionStatus => {
		const jobDocuments = get().jobDocuments;

		if (
			jobDocumentType === JobDocumentTypeEnum.Quotation ||
			jobDocumentType === JobDocumentTypeEnum.CreditNote ||
			jobDocumentType === JobDocumentTypeEnum.Tracing
		) {
			return {
				isAllowed: true,
				explanation: "",
			};
		} else if (jobDocumentType === JobDocumentTypeEnum.DeliveryNote) {
			const deliveryNoteExists = Object.values(jobDocuments).some(
				(jobDocument) =>
					jobDocument.type === JobDocumentTypeEnum.DeliveryNote &&
					jobDocument.status !== JobStatusEnum.ARCHIVED
			);

			if (!deliveryNoteExists) {
				return {
					isAllowed: true,
					explanation: "",
				};
			}

			return {
				isAllowed: false,
				explanation:
					"Es existiert bereits ein Lieferschein. Machen Sie diesen ungültig, um einen neuen zu erstellen.",
			};
		}

		return {
			isAllowed: false,
			explanation: "Die Art des Auftrags wurde nicht erkannt.",
		};
	},
	isDeleteJobDocumentAllowed: (id: number) => {
		const jobDocument = get().jobDocuments[id];
		if (!jobDocument) {
			return {
				isAllowed: false,
				explanation: "Das Dokument konnte nicht gefunden werden.",
			};
		}
		let isAllowed = true;
		let explanation = "";

		if (
			jobDocument.status === JobStatusEnum.PAID_MONTHLY ||
			jobDocument.status === JobStatusEnum.PAID_SINGLE
		) {
			isAllowed = false;
			explanation =
				"Das Dokument kann nicht in den Papierkorb verschoben werden, da es bereits bezahlt wurde.";
		} else if (
			jobDocument.status === JobStatusEnum.BOOKED_MONTHLY ||
			jobDocument.status === JobStatusEnum.BOOKED_SINGLE
		) {
			isAllowed = false;
			explanation =
				"Das Dokument kann nicht in den Papierkorb verschoben werden, da es bereits verbucht wurde.";
		} else if (jobDocument.status === JobStatusEnum.COMPLETED) {
			isAllowed = false;
			explanation =
				"Das Dokument kann nicht in den Papierkorb verschoben werden, da es bereits abgeschlossen ist.";
		}
		return {
			isAllowed,
			explanation,
		};
	},
	deleteJobDocumentLocally: (id: number) => {
		set(
			produce((state) => {
				const { [id]: deleted, ...remainingDocuments } =
					state.jobDocuments;
				state.jobDocuments = remainingDocuments;

				const remainingDocumentsOfSameType = Object.values(
					remainingDocuments as Record<number, JobDocumentWithFiles>
				).filter(
					(doc: JobDocumentWithFiles) => doc.type === deleted.type
				);

				if (remainingDocumentsOfSameType.length === 0) {
					const newTypes = state.job.job_document_types.filter(
						(type: JobDocumentTypeEnum) => type !== deleted.type
					);
					state.job.job_document_types = newTypes;
					adjustJobStage(state, state.job.id, newTypes);
				}
			})
		);
	},
	updateJobDocumentTitle: async (
		id: number,
		title: string,
		status: JobStatusEnum
	) => {
		if (!id) {
			Logger.error(
				"updateJobDocumentTitle called with null/undefined id"
			);
			return;
		}

		if (status > JobStatusEnum.IN_PROGRESS) {
			showNotification({
				message:
					"Der Titel des Dokuments kann nicht geändert werden, da der Auftrag bereits abgeschlossen ist.",
				type: "error",
			});
			return;
		}

		const previousTitle = get().jobDocuments[id].title;
		if (previousTitle === title) return;

		set(
			produce((state) => {
				state.jobDocuments[id].title = title;
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({ title })
			.eq("id", id)
			.select();

		if (error) {
			set(
				produce((state) => {
					state.jobDocuments[id].title = previousTitle;
				})
			);
			showNotification({
				message: "Fehler beim Aktualisieren des Titels des Dokuments",
				type: "error",
			});
			Logger.warn("Error updating job document title", { error });
		} else {
			showNotification({
				message: "Titel des Dokuments aktualisiert",
				type: "success",
			});
		}
	},
	updateJobDocumentNote: async (
		id: number,
		note: string | null,
		status: JobStatusEnum
	) => {
		if (!id) {
			Logger.error("updateJobDocumentNote called with null/undefined id");
			return;
		}

		if (status > JobStatusEnum.IN_PROGRESS) {
			showNotification({
				message:
					"Der Hinweis des Dokuments kann nicht geändert werden, da der Auftrag bereits abgeschlossen ist.",
				type: "error",
			});
			return;
		}

		const previousNote = get().jobDocuments[id].note;
		if (previousNote === note) return;

		set(
			produce((state) => {
				state.jobDocuments[id].note = note;
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({ note: note || null })
			.eq("id", id)
			.select();

		if (error) {
			set(
				produce((state) => {
					state.jobDocuments[id].note = previousNote;
				})
			);
			showNotification({
				message:
					"Fehler beim Aktualisieren des Hinweises des Dokuments",
				type: "error",
			});
			Logger.warn("Error updating job document title", { error });
		} else {
			showNotification({
				message: "Hinweis des Dokuments aktualisiert",
				type: "success",
			});
		}
	},
	updateJobDocumentStatus: async (
		documentId: number,
		status: JobStatusEnum | KvStatusEnum | LsStatusEnum | GsStatusEnum,
		amount: number | null = null,
		accountingDocumentId: number | null = null
	) => {
		if (!documentId) {
			Logger.error(
				"updateJobDocumentStatus called with null/undefined documentId"
			);
			return false;
		}

		if (
			status === JobStatusEnum.BOOKED_SINGLE ||
			status === JobStatusEnum.BOOKED_MONTHLY
		) {
			if (!accountingDocumentId) {
				Logger.error("No accounting document id provided");
				showNotification({
					message:
						"Es fehlt ein Buchhaltungseintrag, um den Status des Dokuments zu aktualisieren.",
					type: "error",
				});
			}
		}

		if (status == JobStatusEnum.COMPLETED) {
			// If the amount is null, show an error but continue with the update
			if (amount === null) {
				Logger.error("No amount provided");
				showNotification({
					message:
						"Um den Auftrag abzuschliessen, muss ein Gesamtbetrag vorhanden sein.",
					type: "warning",
				});
			}
			// If the amount is 0, show a warning and continue with the update
			if (amount === 0) {
				showNotification({
					message: "Gesamtbetrag ist 0.",
					type: "warning",
				});
			}
		}

		const previousJobDocument = get().jobDocuments[documentId];

		set(
			produce((state) => {
				state.jobDocuments[documentId].status = status;
				if (amount) {
					state.jobDocuments[documentId].amount = amount;
				}
				if (accountingDocumentId) {
					state.jobDocuments[documentId].accounting_document_id =
						accountingDocumentId;
				}
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({
				status,
				...(amount ? { amount } : {}),
				...(accountingDocumentId
					? { accounting_document_id: accountingDocumentId }
					: {}),
			})
			.eq("id", documentId);

		if (error) {
			set(
				produce((state) => {
					state.jobDocuments[documentId] = previousJobDocument;
				})
			);
			showNotification({
				message: "Fehler beim Aktualisieren des Status des Dokuments",
				type: "error",
			});
			Logger.error(error, {}, "Error updating job document status");
			return false;
		}

		if (documentDrivesJobStatus(previousJobDocument.type)) {
			await get().syncJobStatus(previousJobDocument.job_id);
		}
		return true;
	},
	updateJobDocumentDate: async (
		documentId: number,
		status: JobStatusEnum,
		date: string
	) => {
		if (!documentId) {
			Logger.error(
				"updateJobDocumentDate called with null/undefined documentId"
			);
			return;
		}

		if (!date) {
			showNotification({
				message: "Das Datum darf nicht leer sein",
				type: "warning",
			});
			return;
		}

		if (status !== JobStatusEnum.IN_PROGRESS) {
			showNotification({
				message:
					"Das Datum kann nur bearbeitet werden, wenn der Auftrag noch nicht abgeschlossen ist",
				type: "error",
			});
			return;
		}

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({ date })
			.eq("id", documentId);

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren des Datums des Dokuments",
				type: "error",
			});
		} else {
			showNotification({
				message: "Datum des Dokuments aktualisiert",
				type: "success",
			});
			set(
				produce((state) => {
					state.jobDocuments[documentId].date = date;
				})
			);
		}
	},
	fetchJobDocument: async (id: number) => {
		if (!id) {
			Logger.error("fetchJobDocument called with null/undefined id");
			return null;
		}

		const { data, error } = await supabase
			.from("job_documents")
			.select(`*, files_with_shares(*)`)
			.eq("id", id)
			.is("deleted_at", null)
			.single();

		if (error) {
			Logger.error(`[job-documents.store] fetchJobDocument: ${error}`);
			return null;
		}

		if (!data) {
			Logger.error(
				`[job-documents.store] fetchJobDocument: No data found for id ${id}`
			);
			return null;
		}

		set(
			produce((state) => {
				state.jobDocuments[id] = data;
			})
		);

		return data;
	},
	closeSidebar: () => set({ openedSidebar: null }),
	openSideBar: (documentId: number, type: OpenedSidebarType) => {
		if (get().openedSidebar?.type !== type) {
			set({
				openedSidebar: {
					documentId,
					type,
				},
			});
		} else {
			set({
				openedSidebar: null,
			});
		}
	},
	setSelectedDocumentId: (id: number | null) => {
		set({ selectedDocumentId: id });
	},
	updateDocumentDiscount: async (
		documentId: number,
		field: "discount_work" | "discount_material",
		value: number
	) => {
		const doc = get().jobDocuments[documentId];
		if (!doc) return;

		const previousValue = doc[field];
		set(
			produce((state) => {
				state.jobDocuments[documentId][field] = value;
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOB_DOCUMENTS)
			.update({ [field]: value })
			.eq("id", documentId);

		if (error) {
			set(
				produce((state) => {
					state.jobDocuments[documentId][field] = previousValue;
				})
			);
			showNotification({
				message: "Fehler beim Aktualisieren des Rabatts",
				type: "error",
			});
			Logger.error(error, {}, "Error updating document discount");
		}
	},
});
